<div class="dashboards-page">
  @if (viewMode !== 'detail') {
    <header class="page-header">
      <div>
        <h1>Dashboards</h1>
        @if (canManageDashboards) {
          <p class="subtitle">
            Build saved dashboard views from scheduled report previews, render KPI tiles, and add buttons that jump into
            the existing report workspace.
          </p>
        } @else {
          <p class="subtitle">View dashboards that have been shared with you.</p>
        }
      </div>
      <div class="header-actions">
        @if (viewMode !== 'list') {
          <button mat-stroked-button type="button" (click)="returnToList()">Back to Dashboards</button>
        }
        @if (viewMode === 'list' && canManageDashboards) {
          <button mat-flat-button color="primary" type="button" (click)="startCreate()">
            Create Dashboard
          </button>
        }
      </div>
    </header>
  }

  @if (isLoading || isBootstrapping) {
    <section class="page-loading">
      <mat-spinner [diameter]="32"></mat-spinner>
      <span>Loading dashboards</span>
    </section>
  }

  @if (!isLoading && !isBootstrapping) {
    @if (viewMode === 'list') {
      <section class="list-shell">
        <div class="toolbar">
          <mat-form-field appearance="outline">
            <mat-label>Search dashboards</mat-label>
            <input
              matInput
              [(ngModel)]="searchValue"
              [placeholder]="canManageDashboards ? 'Name, description, tiles, actions' : 'Name or description'"
              />
          </mat-form-field>
          @if (canManageDashboards) {
            <mat-form-field appearance="outline">
              <mat-label>Status</mat-label>
              <mat-select [(ngModel)]="statusFilter">
                <mat-option value="all">All statuses</mat-option>
                <mat-option value="draft">Draft</mat-option>
                <mat-option value="active">Active</mat-option>
                <mat-option value="archived">Archived</mat-option>
              </mat-select>
            </mat-form-field>
          }
          <button mat-stroked-button type="button" (click)="refreshList()">Refresh</button>
        </div>
        @if (filteredDashboards.length) {
          <div class="dashboard-grid">
            @for (dashboard of filteredDashboards; track trackByDashboardId($index, dashboard)) {
              <article class="dashboard-card">
                <div class="dashboard-card-head">
                  <div>
                    <h2>{{ dashboard.name }}</h2>
                    @if (dashboard.description) {
                      <p>{{ dashboard.description }}</p>
                    }
                  </div>
                  @if (canManageDashboards) {
                    <span
                      class="status-pill"
                      [class.active]="dashboard.status === 'active'"
                      [class.archived]="dashboard.status === 'archived'"
                      >
                      {{ dashboard.status }}
                    </span>
                  }
                </div>
                <div class="dashboard-card-meta">
                  <div>
                    <span class="meta-label">Tiles</span>
                    <strong>{{ dashboard.tiles.length }}</strong>
                  </div>
                  @if (canManageDashboards) {
                    <div>
                      <span class="meta-label">Buttons</span>
                      <strong>{{ dashboard.actions.length }}</strong>
                    </div>
                  }
                  <div>
                    <span class="meta-label">Updated</span>
                    <strong>{{ dashboard.updatedAt | date:'short' }}</strong>
                  </div>
                  @if (canManageDashboards) {
                    <div>
                      <span class="meta-label">Permissions</span>
                      <strong>{{ dashboard.permissionCount || 0 }}</strong>
                    </div>
                  }
                </div>
                <div class="dashboard-card-actions">
                  <button mat-stroked-button type="button" (click)="openDashboard(dashboard)">
                    {{ canManageDashboards ? 'Open' : 'View' }}
                  </button>
                  @if (canManageDashboards) {
                    <button mat-stroked-button type="button" (click)="editDashboard(dashboard)">Edit</button>
                  }
                  @if (canManageDashboards) {
                    <button mat-button color="warn" type="button" (click)="confirmDeleteDashboard(dashboard, $event)">Delete</button>
                  }
                </div>
              </article>
            }
          </div>
        } @else {
          <section class="empty-state">
            <mat-icon>dashboard_customize</mat-icon>
            <h2>{{ canManageDashboards ? 'No dashboards yet' : 'No dashboards available' }}</h2>
            <p>
              {{
              canManageDashboards
              ? 'Create a dashboard to start surfacing rolled-up report metrics and quick links.'
              : 'No dashboards have been shared with you yet.'
              }}
            </p>
          </section>
        }
      </section>
    }
    @if (viewMode === 'create' || viewMode === 'edit') {
      <section class="editor-shell">
        <form [formGroup]="dashboardForm" class="editor-grid">
          <article class="panel">
            <div class="panel-header">
              <span class="panel-step">1</span>
              <div>
                <h2>{{ viewMode === 'create' ? 'Create dashboard' : 'Edit dashboard' }}</h2>
                <p>Name the dashboard and choose whether it is still a draft or ready for use.</p>
              </div>
            </div>
            <div class="form-grid two-up">
              <mat-form-field appearance="outline">
                <mat-label>Dashboard name</mat-label>
                <input matInput formControlName="name" />
              </mat-form-field>
              <mat-form-field appearance="outline">
                <mat-label>Status</mat-label>
                <mat-select formControlName="status">
                  <mat-option value="draft">Draft</mat-option>
                  <mat-option value="active">Active</mat-option>
                  <mat-option value="archived">Archived</mat-option>
                </mat-select>
              </mat-form-field>
              <mat-form-field appearance="outline" class="full-width">
                <mat-label>Description</mat-label>
                <textarea matInput rows="3" formControlName="description"></textarea>
              </mat-form-field>
            </div>
          </article>
          <article class="panel">
            <div class="panel-header">
              <span class="panel-step">2</span>
              <div>
                <h2>Tiles</h2>
                <p>Each tile reads from a saved scheduled report preview and renders one summary metric.</p>
              </div>
            </div>
            @if (!tilesArray.length) {
              <div class="empty-inline-state">
                Add at least one tile to surface a rolled-up metric.
              </div>
            }
            @for (tile of tilesArray.controls; track trackByControlId(i, tile); let i = $index) {
              <div class="selected-card" [formGroup]="asFormGroup(tile)">
                <div class="selected-card-head">
                  <div class="selected-card-copy">
                    <strong>{{ tile.get('title')?.value || ('Tile ' + (i + 1)) }}</strong>
                    <p>{{ getReportName($safeNavigationMigration(tile.get('reportId')?.value)) }}</p>
                  </div>
                  <button mat-icon-button type="button" (click)="removeTile(i)" aria-label="Remove Tile">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
                <div class="form-grid three-up compact">
                  <mat-form-field appearance="outline">
                    <mat-label>Tile title</mat-label>
                    <input matInput formControlName="title" />
                    @if (tile.get('title')?.hasError('required') && tile.get('title')?.touched) {
                      <mat-error>
                        Tile title is required.
                      </mat-error>
                    }
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Saved report</mat-label>
                    <mat-select formControlName="reportId" (selectionChange)="onTileReportChange(tile)">
                      @for (report of reports; track trackByReportId($index, report)) {
                        <mat-option [value]="report.id">
                          {{ report.name }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Metric</mat-label>
                    <mat-select formControlName="metricType" (selectionChange)="onTileMetricTypeChange(tile)">
                      @for (metric of metricTypeOptions; track metric) {
                        <mat-option [value]="metric.value">{{ metric.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Description</mat-label>
                    <input matInput formControlName="description" />
                  </mat-form-field>
                  @if (tile.get('metricType')?.value !== 'count') {
                    <mat-form-field appearance="outline">
                      <mat-label>Preview column</mat-label>
                      @if (getTileFieldOptions(tile).length) {
                        <mat-select formControlName="fieldKey">
                          @for (option of getTileFieldOptions(tile); track option) {
                            <mat-option [value]="option.value">{{ option.label }}</mat-option>
                          }
                        </mat-select>
                      }
                      @if (!getTileFieldOptions(tile).length) {
                        <input
                          matInput
                          formControlName="fieldKey"
                          placeholder="Preview column header or field"
                          />
                      }
                      <mat-hint>{{ isTileFieldOptionsLoading(tile) ? 'Loading preview columns...' : getTileFieldHint(tile) }}</mat-hint>
                    </mat-form-field>
                  }
                  <mat-form-field appearance="outline">
                    <mat-label>Display format</mat-label>
                    <mat-select formControlName="valueFormat">
                      @for (format of valueFormatOptions; track format) {
                        <mat-option [value]="format.value">{{ format.label }}</mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Precision</mat-label>
                    <input matInput type="number" min="0" max="4" formControlName="precision" />
                  </mat-form-field>
                </div>
              </div>
            }
            <div class="builder-footer">
              <button mat-stroked-button type="button" (click)="addTile()">Add Tile</button>
            </div>
          </article>
          <article class="panel">
            <div class="panel-header">
              <span class="panel-step">3</span>
              <div>
                <h2>Report buttons</h2>
                <p>Create buttons that open an existing saved report or start a new report from a template.</p>
              </div>
            </div>
            @if (!actionsArray.length) {
              <div class="empty-inline-state">
                Add action buttons when the dashboard should jump directly into the report workspace.
              </div>
            }
            @for (action of actionsArray.controls; track trackByControlId(i, action); let i = $index) {
              <div class="selected-card" [formGroup]="asFormGroup(action)">
                <div class="selected-card-head">
                  <div class="selected-card-copy">
                    <strong>{{ action.get('label')?.value || ('Action ' + (i + 1)) }}</strong>
                    <p>{{ action.get('targetType')?.value === 'saved_report' ? getReportName($safeNavigationMigration(action.get('reportId')?.value)) : getTemplateLabel($safeNavigationMigration(action.get('templateKey')?.value)) }}</p>
                  </div>
                  <button mat-icon-button type="button" (click)="removeAction(i)" aria-label="Remove Report Button">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
                <div class="form-grid three-up compact">
                  <mat-form-field appearance="outline">
                    <mat-label>Button label</mat-label>
                    <input matInput formControlName="label" />
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>Button type</mat-label>
                    <mat-select formControlName="targetType">
                      <mat-option value="saved_report">Open saved report</mat-option>
                      <mat-option value="report_template">Start template report</mat-option>
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline" class="full-width">
                    <mat-label>Description</mat-label>
                    <input matInput formControlName="description" />
                  </mat-form-field>
                  @if (action.get('targetType')?.value === 'saved_report') {
                    <mat-form-field appearance="outline">
                      <mat-label>Saved report</mat-label>
                      <mat-select formControlName="reportId">
                        @for (report of reports; track report) {
                          <mat-option [value]="report.id">{{ report.name }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                  @if (action.get('targetType')?.value === 'report_template') {
                    <mat-form-field appearance="outline">
                      <mat-label>Report template</mat-label>
                      <mat-select formControlName="templateKey">
                        @for (template of templates; track template) {
                          <mat-option [value]="template.key">{{ template.label }}</mat-option>
                        }
                      </mat-select>
                    </mat-form-field>
                  }
                </div>
              </div>
            }
            <div class="builder-footer">
              <button mat-stroked-button type="button" (click)="addAction()">Add Report Button</button>
            </div>
          </article>
          <article class="panel">
            <div class="panel-header">
              <span class="panel-step">4</span>
              <div>
                <h2>Dashboard access</h2>
                <p>Grant this dashboard to specific users, companies, teams, or brands.</p>
              </div>
            </div>
            @if (isLoadingPermissions) {
              <div class="inline-loading compact-loading">
                <mat-spinner [diameter]="24"></mat-spinner>
                <span>Loading dashboard permissions</span>
              </div>
            }
            @if (!isLoadingPermissions && !permissionsArray.length) {
              <div class="empty-inline-state">
                No access entries have been added. Superadmins can still manage this dashboard.
              </div>
            }
            @for (permission of permissionsArray.controls; track trackByPermissionControl(i, permission); let i = $index) {
              <div
                class="selected-card permission-card"
                [formGroup]="asFormGroup(permission)"
                >
                <div class="selected-card-head">
                  <div class="selected-card-copy">
                    <strong>{{ getPermissionSubjectTypeLabel($safeNavigationMigration(permission.get('subjectType')?.value)) }}</strong>
                    <p>{{ getPermissionSubjectSelectionLabel(permission) }}</p>
                  </div>
                  <button mat-icon-button type="button" (click)="removePermission(i)" aria-label="Remove Dashboard Access">
                    <mat-icon>close</mat-icon>
                  </button>
                </div>
                <div class="form-grid two-up compact">
                  <mat-form-field appearance="outline">
                    <mat-label>Access type</mat-label>
                    <mat-select formControlName="subjectType" (selectionChange)="onPermissionSubjectTypeChange(permission)">
                      @for (option of permissionSubjectTypeOptions; track option) {
                        <mat-option [value]="option.value">
                          {{ option.label }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                  <mat-form-field appearance="outline">
                    <mat-label>{{ getPermissionSubjectTypeLabel($safeNavigationMigration(permission.get('subjectType')?.value)) }}</mat-label>
                    <mat-select
                      formControlName="subjectId"
                      panelClass="member-addedit-select-search-panel"
                      (openedChange)="onPermissionSubjectSelectOpened(permission, $event)"
                      >
                      <mat-select-trigger>
                        {{ getPermissionSubjectSelectionLabel(permission) }}
                      </mat-select-trigger>
                      @if (shouldShowSelectSearch(getPermissionSubjectOptions($safeNavigationMigration(permission.get('subjectType')?.value)).length)) {
                        <mat-option
                          class="contains-mat-select-search"
                          [disabled]="true"
                          >
                          <ngx-mat-select-search
                            [formControl]="getPermissionSearchControl($safeNavigationMigration(permission.get('subjectType')?.value))"
                            noEntriesFoundLabel="No matches"
                            placeholderLabel="Search..."
                          ></ngx-mat-select-search>
                        </mat-option>
                      }
                      @if (isPermissionOptionsLoading($safeNavigationMigration(permission.get('subjectType')?.value))) {
                        <mat-option [disabled]="true">
                          Loading {{ getPermissionSubjectLoadingLabel($safeNavigationMigration(permission.get('subjectType')?.value)) }}...
                        </mat-option>
                      }
                      @for (option of getFilteredPermissionSubjectOptions($safeNavigationMigration(permission.get('subjectType')?.value)); track trackByPermissionOption($index, option)) {
                        <mat-option
                          [value]="option.id"
                          >
                          {{ option.label }}@if (option.secondaryLabel) {
                          <span> - {{ option.secondaryLabel }}</span>
                        }
                      </mat-option>
                    }
                  </mat-select>
                  @if (permission.get('subjectId')?.hasError('required') && permission.get('subjectId')?.touched) {
                    <mat-error>
                      {{ getPermissionSubjectTypeLabel($safeNavigationMigration(permission.get('subjectType')?.value)) }} is required.
                    </mat-error>
                  }
                </mat-form-field>
              </div>
            </div>
          }
          <div class="builder-footer">
            <button mat-stroked-button type="button" (click)="addPermission()" [disabled]="isLoadingPermissions">Add Access</button>
          </div>
        </article>
        <article class="panel preview-panel">
          <div class="panel-header">
            <span class="panel-step">5</span>
            <div>
              <h2>Preview</h2>
              <p>Render the current dashboard definition against the selected scheduled reports.</p>
            </div>
          </div>
          @if (validationMessages.length) {
            <div class="validation-list">
              <h3>Fix these items first</h3>
              <ul>
                @for (message of validationMessages; track message) {
                  <li>{{ message }}</li>
                }
              </ul>
            </div>
          }
          @if (isRendering) {
            <div class="inline-loading">
              <mat-spinner [diameter]="28"></mat-spinner>
              <span>Rendering dashboard preview</span>
            </div>
          }
          @if (!isRendering && !editorRenderState) {
            <div class="empty-inline-state">
              Preview the dashboard to validate tile values before saving it.
            </div>
          }
          @if (editorRenderState) {
            @if (editorRenderState.warnings.length) {
              <div class="warning-list">
                <h3>Preview warnings</h3>
                <ul>
                  @for (warning of editorRenderState.warnings; track warning) {
                    <li>{{ warning }}</li>
                  }
                </ul>
              </div>
            }
            <div class="tile-grid">
              @for (tile of editorRenderState.tiles; track trackByRenderedTile($index, tile)) {
                <article class="tile-card">
                  <div class="tile-card-head">
                    <div>
                      <h3>{{ tile.title }}</h3>
                      @if (tile.reportDescription) {
                        <p class="report-description">{{ tile.reportDescription }}</p>
                      }
                    </div>
                    <div class="tile-card-actions">
                      @if (tile.status === 'success') {
                        <button mat-button type="button" (click)="toggleTilePreview(tile)">
                          {{ isTilePreviewOpen(tile) ? 'Hide Preview' : 'Preview Results' }}
                        </button>
                      }
                      @if (tile.status === 'success') {
                        <button mat-button type="button" (click)="sendTileReport(tile)">Send Report</button>
                      }
                      @if (tile.reportId) {
                        <button mat-button type="button" (click)="openTileReport(tile)">Open Report</button>
                      }
                    </div>
                  </div>
                  <strong class="tile-value" [class.error]="tile.status === 'error'">{{ tile.displayValue }}</strong>
                  @if (tile.status === 'success') {
                    <p class="tile-helper">{{ tile.helperText }}</p>
                  }
                  @if (tile.status === 'error') {
                    <p class="tile-helper error-text">{{ tile.errorMessage }}</p>
                  }
                  @if (tile.status === 'success' && isTilePreviewOpen(tile)) {
                    <div class="tile-preview">
                      <p class="tile-preview-note">{{ getTilePreviewSummary(tile) }}</p>
                      @if (!tile.previewRows.length) {
                        <div class="empty-inline-state compact-empty">
                          No preview rows returned for this report.
                        </div>
                      }
                      @if (tile.previewRows.length) {
                        <div class="table-shell">
                          <table class="preview-table">
                            <thead>
                              <tr>
                                @for (header of getTilePreviewHeaders(tile); track header) {
                                  <th>
                                    <button type="button" class="preview-sort-button" (click)="toggleTilePreviewSort(tile, header)">
                                      <span>{{ header }}</span>
                                      @if (getTilePreviewSortDirection(tile, header) === 'asc') {
                                        <span class="preview-sort-indicator">arrow_upward</span>
                                      }
                                      @if (getTilePreviewSortDirection(tile, header) === 'desc') {
                                        <span class="preview-sort-indicator">arrow_downward</span>
                                      }
                                    </button>
                                  </th>
                                }
                              </tr>
                            </thead>
                            <tbody>
                              @for (row of getSortedTilePreviewRows(tile); track row) {
                                <tr>
                                  @for (header of getTilePreviewHeaders(tile); track header) {
                                    <td>
                                      {{ getTilePreviewCellText(tile, getTilePreviewCellValue(tile, row, header)) }}
                                    </td>
                                  }
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      }
                    </div>
                  }
                </article>
              }
            </div>
          }
        </article>
      </form>
      <div class="editor-actions">
        <button mat-stroked-button type="button" (click)="previewDashboard()">Preview Dashboard</button>
        <button mat-flat-button color="primary" type="button" (click)="saveDashboard()" [disabled]="isSaving">
          {{ isSaving ? 'Saving...' : 'Save Dashboard' }}
        </button>
      </div>
    </section>
  }
  @if (viewMode === 'detail' && detailDashboard) {
    <section class="detail-shell">
      <div class="detail-topline">
        <div>
          <h2>{{ detailDashboard.name }}</h2>
          @if (detailDashboard.description) {
            <p class="subtitle">{{ detailDashboard.description }}</p>
          }
        </div>
        <div class="header-actions">
          @if (canManageDashboards) {
            <span
              class="status-pill"
              [class.active]="detailDashboard.status === 'active'"
              [class.archived]="detailDashboard.status === 'archived'"
              >
              {{ detailDashboard.status }}
            </span>
          }
          <button mat-stroked-button type="button" (click)="refreshDetail()">Refresh</button>
          <button mat-stroked-button type="button" (click)="returnToList()">Back to Dashboards</button>
          @if (canManageDashboards) {
            <button mat-stroked-button type="button" (click)="editDashboard(detailDashboard)">Edit</button>
          }
          @if (canManageDashboards) {
            <button mat-button color="warn" type="button" (click)="confirmDeleteDashboard(detailDashboard)">Delete</button>
          }
        </div>
      </div>
      @if (canManageDashboards && detailDashboard.actions.length) {
        <div class="dashboard-actions-bar">
          @for (action of detailDashboard.actions; track action) {
            <button mat-raised-button color="accent" type="button" (click)="openAction(action)">
              {{ action.label }}
            </button>
          }
        </div>
      }
      @if (isRendering && !detailRenderState) {
        <div class="inline-loading">
          <mat-spinner [diameter]="28"></mat-spinner>
          <span>Rendering dashboard</span>
        </div>
      }
      @if (detailRenderState?.warnings?.length) {
        <div class="warning-list">
          <h3>Render warnings</h3>
          <ul>
            @for (warning of detailRenderState?.warnings; track warning) {
              <li>{{ warning }}</li>
            }
          </ul>
        </div>
      }
      @if (detailRenderState && !detailRenderState.tiles.length) {
        <div class="empty-inline-state">
          {{ canManageDashboards ? 'This dashboard has no tiles yet. Edit it to add metrics.' : 'This dashboard has no tiles available.' }}
        </div>
      }
      @if (detailRenderState?.tiles?.length) {
        <div class="tile-grid">
          @for (tile of detailRenderState?.tiles; track trackByRenderedTile($index, tile)) {
            <article class="tile-card">
              <div class="tile-card-head">
                <div>
                  <h3>{{ tile.title }}</h3>
                  @if (tile.reportDescription) {
                    <p class="report-description">{{ tile.reportDescription }}</p>
                  }
                </div>
                <div class="tile-card-actions">
                  @if (tile.status === 'success') {
                    <button mat-button type="button" (click)="toggleTilePreview(tile)">
                      {{ isTilePreviewOpen(tile) ? 'Hide Preview' : 'Preview Results' }}
                    </button>
                  }
                  @if (canManageDashboards && tile.status === 'success') {
                    <button mat-button type="button" (click)="sendTileReport(tile)">Send Report</button>
                  }
                  @if (canManageDashboards && tile.reportId) {
                    <button mat-button type="button" (click)="openTileReport(tile)">Open Report</button>
                  }
                </div>
              </div>
              <strong class="tile-value" [class.error]="tile.status === 'error'">{{ tile.displayValue }}</strong>
              @if (tile.status === 'success') {
                <p class="tile-helper">{{ tile.helperText }}</p>
              }
              @if (tile.status === 'error') {
                <p class="tile-helper error-text">{{ tile.errorMessage }}</p>
              }
              @if (tile.warnings.length) {
                <ul class="tile-warning-list">
                  @for (warning of tile.warnings; track warning) {
                    <li>{{ warning }}</li>
                  }
                </ul>
              }
              @if (tile.status === 'success' && isTilePreviewOpen(tile)) {
                <div class="tile-preview">
                  <div class="tile-preview-toolbar">
                    <p class="tile-preview-note">{{ getTilePreviewSummary(tile) }}</p>
                    @if (tile.reportId) {
                      <button
                        mat-stroked-button
                        type="button"
                        [disabled]="isTileReportDownloading(tile)"
                        (click)="downloadTileReport(tile)"
                        >
                        {{ isTileReportDownloading(tile) ? 'Downloading...' : 'Download Report' }}
                      </button>
                    }
                  </div>
                  @if (!tile.previewRows.length) {
                    <div class="empty-inline-state compact-empty">
                      No preview rows returned for this report.
                    </div>
                  }
                  @if (tile.previewRows.length) {
                    <div class="table-shell">
                      <table class="preview-table">
                        <thead>
                          <tr>
                            @for (header of getTilePreviewHeaders(tile); track header) {
                              <th>
                                <button type="button" class="preview-sort-button" (click)="toggleTilePreviewSort(tile, header)">
                                  <span>{{ header }}</span>
                                  @if (getTilePreviewSortDirection(tile, header) === 'asc') {
                                    <span class="preview-sort-indicator">arrow_upward</span>
                                  }
                                  @if (getTilePreviewSortDirection(tile, header) === 'desc') {
                                    <span class="preview-sort-indicator">arrow_downward</span>
                                  }
                                </button>
                              </th>
                            }
                          </tr>
                        </thead>
                        <tbody>
                          @for (row of getSortedTilePreviewRows(tile); track row) {
                            <tr>
                              @for (header of getTilePreviewHeaders(tile); track header) {
                                <td>
                                  {{ getTilePreviewCellText(tile, getTilePreviewCellValue(tile, row, header)) }}
                                </td>
                              }
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  }
                </div>
              }
            </article>
          }
        </div>
      }
      @if (detailRenderState) {
        <div class="detail-meta">
          <span><strong>Rendered:</strong> {{ detailRenderState.renderedAt | date:'medium' }}</span>
          <span><strong>Tiles:</strong> {{ detailRenderState.tiles.length }}</span>
        </div>
      }
    </section>
  }
}
</div>
